<template>
  <div class="participant-shell">
    <div
      v-if="isMobile && !sidebarHidden"
      class="sidebar-backdrop"
      @click="sidebarHidden = true"
    />

    <aside class="sidebar-widget" :class="{ show: sidebarHidden }">
      <div class="system-brand">
        <button
          v-if="isSidebarOpen"
          type="button"
          class="menu-btn"
          aria-label="Close menu"
          :aria-expanded="true"
          @click="toggleSidebar"
        >
          <img src="@/assets/images/icons/white-main-menu.svg" alt="" />
        </button>
        <div class="brand">
          <img class="logo" src="@/assets/images/logo.svg" alt="Watch" />
        </div>
      </div>
      <div class="menu-list">
        <ul>
          <li class="divider">Participant</li>
          <li
            v-for="item in navItems"
            :key="item.id"
            class="item"
            :class="{ active: activeTab === item.id }"
            @click="switchTab(item.id)"
          >
            <b-icon :icon="item.icon" class="icon" size="is-small" />
            <span class="text">{{ item.label }}</span>
          </li>
        </ul>
      </div>
      <div class="sidebar-user" v-if="participant.name">
        <span class="code">Code #{{ participant.item_index }}</span>
        <span class="name crop-text">{{ participant.name }}</span>
      </div>
    </aside>

    <div class="body-container" :class="{ nosidebar: sidebarHidden }">
      <header class="navigation">
        <div class="nav-brand">
          <button
            v-if="!isSidebarOpen"
            type="button"
            class="menu-btn"
            aria-label="Open menu"
            :aria-expanded="false"
            @click="toggleSidebar"
          >
            <img src="@/assets/images/icons/black-main-menu.svg" class="bl-menu" alt="" />
            <img src="@/assets/images/icons/white-main-menu.svg" class="wh-menu" alt="" />
          </button>
          <span class="page-title">{{ currentPageTitle }}</span>
        </div>
        <div class="nav-options">
          <div class="btn-container">
            <button
              type="button"
              ref="profile-menu"
              class="nav-button profile-btn"
              @click="showProfileMenu = !showProfileMenu"
            >
              <img
                v-if="participant.image"
                class="profile-logo"
                :src="$store.state.ASSETS_BASE_URL + '/assets/uploaded/' + participant.image"
                alt=""
              />
              <div v-else class="empty-profile">{{ nameInitials }}</div>
            </button>
            <div
              v-if="showProfileMenu"
              v-closable="{ exclude: ['profile-menu'], handler: 'closeProfileMenu' }"
              class="settings-drop-down"
            >
              <div class="profile-widget">
                <strong>{{ participant.name || 'Participant' }}</strong>
                <p v-if="participant.email">{{ participant.email }}</p>
              </div>
              <div class="setting-item" @click="goToProfile">
                <b-icon icon="account-edit" size="is-small" class="icon" />
                <span>Edit profile</span>
              </div>
              <div class="setting-item" @click="handleLogout">
                <b-icon icon="logout" size="is-small" class="icon" />
                <span>Logout</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main class="page-content">
        <OverviewTab
          v-if="activeTab === 'overview'"
          :participant="participant"
          :dashboard-data="dashboardData"
          :percentage-reward="percentageReward"
          :expected-reward="expectedReward"
          :earnings-data="earningsData"
          :is-percentage-reward="isPercentageReward"
          :current-earnings="currentEarnings"
          :projected-earnings="projectedEarnings"
          :total-earnings="totalEarnings"
        />

        <ProfileTab
          v-if="activeTab === 'profile'"
          :participant="participant"
          @updated="onProfileUpdated"
        />

        <TransactionsTab v-if="activeTab === 'transactions'" />

        <InvoicesTab v-if="activeTab === 'invoices'" />
      </main>
    </div>
  </div>
</template>

<script>
import OverviewTab from './components/OverviewTab.vue'
import ProfileTab from './components/ProfileTab.vue'
import TransactionsTab from './components/TransactionsTab.vue'
import InvoicesTab from './components/InvoicesTab.vue'

export default {
  name: 'ParticipantDashboard',
  components: { OverviewTab, ProfileTab, TransactionsTab, InvoicesTab },
  data() {
    return {
      activeTab: 'overview',
      participant: {},
      dashboardData: { votes: 0, reward: null },
      loading: true,
      percentageReward: null,
      expectedReward: null,
      earningsData: [],
      interval: null,
      sidebarHidden: false,
      showProfileMenu: false,
      isMobile: false,
      navItems: [
        { id: 'overview', label: 'Overview', icon: 'view-dashboard' },
        { id: 'profile', label: 'My information', icon: 'account-edit' },
        { id: 'transactions', label: 'Transactions', icon: 'swap-horizontal' },
        { id: 'invoices', label: 'Invoices', icon: 'file-document' },
      ],
    }
  },
  computed: {
    isSidebarOpen() {
      return !this.sidebarHidden
    },
    isPercentageReward() {
      return this.percentageReward !== null
    },
    currentPageTitle() {
      const item = this.navItems.find((n) => n.id === this.activeTab)
      return item ? item.label : 'Dashboard'
    },
    nameInitials() {
      const name = (this.participant.name || 'P').trim()
      const parts = name.split(/\s+/).filter(Boolean)
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.charAt(0).toUpperCase()
    },
    currentEarnings() {
      if (!this.isPercentageReward || !this.earningsData.length) return 0
      const currentWeek = this.earningsData[0]
      return currentWeek ? currentWeek.share : 0
    },
    projectedEarnings() {
      if (!this.isPercentageReward || !this.earningsData.length) return 0
      const validEarnings = this.earningsData.filter((item) => item.share > 0)
      if (validEarnings.length === 0) return 0
      const weeklyAverage = validEarnings.reduce((sum, item) => sum + item.share, 0) / validEarnings.length
      return Math.round(weeklyAverage * 4)
    },
    totalEarnings() {
      if (!this.isPercentageReward || !this.earningsData.length) return 0
      return this.earningsData.reduce((sum, item) => sum + item.share, 0)
    },
  },
  watch: {
    '$route.params.tab'(tab) {
      if (tab) {
        this.activeTab = tab
      }
    },
  },
  mounted() {
    if (this.$route.params.tab) {
      this.activeTab = this.$route.params.tab
    }
    this.syncSidebarForViewport()
    window.addEventListener('resize', this.syncSidebarForViewport)
    this.loadDashboardData()
    this.interval = setInterval(() => {
      if (this.activeTab === 'overview') {
        this.loadDashboardData()
      }
    }, 2000)
  },
  beforeDestroy() {
    clearInterval(this.interval)
    window.removeEventListener('resize', this.syncSidebarForViewport)
  },
  methods: {
    syncSidebarForViewport() {
      this.isMobile = window.innerWidth <= 966
      if (this.isMobile) {
        this.sidebarHidden = true
      } else {
        this.sidebarHidden = false
      }
    },
    toggleSidebar() {
      this.sidebarHidden = !this.sidebarHidden
    },
    closeProfileMenu() {
      this.showProfileMenu = false
    },
    goToProfile() {
      this.showProfileMenu = false
      this.switchTab('profile')
    },
    switchTab(tab) {
      this.activeTab = tab
      if (this.isMobile) {
        this.sidebarHidden = true
      }
      this.$router.push({ path: `/participant/dashboard/${tab}` }).catch(() => {})
    },

    onProfileUpdated(updated) {
      this.participant = { ...this.participant, ...updated }
    },

    async loadDashboardData() {
      try {
        const response = await this.$store.dispatch('PARTICIPANT_GET_DASHBOARD')
        if (response.data.status === 'ok') {
          this.dashboardData = response.data.data
          this.participant = { ...this.participant, ...response.data.data.participant }
          if (response.data.data.percentage_reward) {
            this.percentageReward = response.data.data.percentage_reward
          }
          if (response.data.data.earnings_history) {
            this.earningsData = response.data.data.earnings_history
          }
          if (response.data.data.expected_reward) {
            this.expectedReward = response.data.data.expected_reward
          }
        } else {
          throw new Error(response.data.message || 'Failed to load dashboard data')
        }
      } catch (error) {
        console.error('Dashboard load error:', error)
        if (error.response && error.response.status === 401) {
          this.handleLogout()
        }
      } finally {
        this.loading = false
      }
    },

    async handleLogout() {
      this.showProfileMenu = false
      try {
        await this.$store.dispatch('PARTICIPANT_LOGOUT')
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        localStorage.removeItem('participant_token')
        localStorage.removeItem('participant_data')
        this.$router.push('/participant/login/' + this.participant.voting_session_id)
      }
    },
  },
}
</script>

<style lang="scss">
.participant-shell {
  .sidebar-widget .menu-list ul li.item .icon {
    color: #fff;
    margin-right: 16px;
    width: 18px;
  }
  .sidebar-widget .menu-list ul li.item.active .icon {
    color: var(--wecodefy-secondary-color, #9fc953);
    margin-left: -5px;
  }
}
</style>

<style lang="scss" scoped>
.participant-shell {
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: #fafafa;
}

.sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9999998;
}

.sidebar-widget {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 230px;
  background: var(--wecodefy-bg-color, #011616);
  z-index: 9999999;
  box-shadow: -1px 0 26px rgba(0, 0, 0, 0.4);
  transition: left 700ms ease-in-out;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  &.show {
    left: -280px !important;
  }

  .system-brand {
    width: 100%;
    height: 100px;
    display: flex;
    align-items: center;
    padding: 10px;
    position: sticky;
    top: 0;
    background: inherit;
    z-index: 1;

    .menu-btn {
      border: none;
      background: transparent;
      padding-left: 12px;
      cursor: pointer;
      img { width: 26px; }
    }

    .brand {
      flex: 1;
      height: 100%;
      display: flex;
      justify-content: center;
      .logo {
        height: 100%;
        max-width: 140px;
        object-fit: contain;
      }
    }
  }

  .menu-list {
    flex: 1;
    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li.divider {
        border-bottom: 1px solid #3c3c3c;
        color: #fff;
        padding: 24px 25px 10px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        opacity: 0.7;
      }

      li.item {
        padding: 16px 15px;
        display: flex;
        align-items: center;
        cursor: pointer;
        margin-left: 15px;

        .text {
          color: #fff;
          font-size: 14px;
          font-weight: 500;
        }

        &.active {
          border-left: 5px solid var(--wecodefy-secondary-color, #9fc953);
          .text {
            color: var(--wecodefy-secondary-color, #9fc953);
          }
        }

        &:hover .text {
          color: var(--wecodefy-secondary-color, #9fc953);
        }
      }
    }
  }

  .sidebar-user {
    padding: 16px 20px 24px;
    border-top: 1px solid #3c3c3c;
    .code {
      display: block;
      font-size: 12px;
      color: var(--wecodefy-secondary-color, #9fc953);
      margin-bottom: 4px;
    }
    .name {
      display: block;
      color: #fff;
      font-size: 14px;
    }
  }
}

.body-container {
  position: fixed;
  left: 230px;
  right: 0;
  bottom: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  z-index: 88;
  background: #fafafa;
  transition: left 700ms ease-in-out;

  @media (max-width: 966px) {
    left: 0 !important;
  }

  &.nosidebar {
    @media (min-width: 967px) {
      left: 0 !important;
    }
  }
}

.navigation {
  height: 60px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  background: #fff;
  border-bottom: 1px solid #ddd;
  z-index: 999;

  @media (max-width: 638px) {
    background: var(--wecodefy-bg-color, #011616);
    .page-title { color: #fff; }
  }

  .nav-brand {
    display: flex;
    align-items: center;
    min-width: 0;

    .menu-btn {
      border: none;
      background: transparent;
      padding: 8px 12px;
      cursor: pointer;
      img { width: 26px; }
      .bl-menu {
        @media (max-width: 638px) { display: none; }
      }
      .wh-menu {
        @media (min-width: 639px) { display: none; }
      }
    }

    .page-title {
      font-size: 18px;
      font-weight: 600;
      color: #1a191b;
      margin-left: 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .nav-options {
    display: flex;
    .btn-container {
      position: relative;
      margin-left: 10px;
    }
    .nav-button {
      background: transparent;
      border: none;
      cursor: pointer;
      &.profile-btn img.profile-logo,
      &.profile-btn .empty-profile {
        height: 32px;
        width: 32px;
        border-radius: 50%;
        object-fit: cover;
        border: 3px solid var(--wecodefy-secondary-color, #9fc953);
      }
      .empty-profile {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 600;
        background: #1a191b;
        color: #fff;
      }
    }
    .settings-drop-down {
      position: absolute;
      top: 40px;
      right: 0;
      width: 220px;
      z-index: 9999;
      background: #fff;
      border-radius: 5px;
      border: 1px solid #ddd;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

      .profile-widget {
        padding: 16px;
        text-align: center;
        border-bottom: 1px solid #eee;
        strong { display: block; font-size: 14px; color: #1a191b; }
        p { margin: 6px 0 0; font-size: 12px; color: #797979; word-break: break-word; }
      }
      .setting-item {
        padding: 12px 14px;
        font-size: 13px;
        cursor: pointer;
        border-bottom: 1px solid #eee;
        display: flex;
        align-items: center;
        gap: 10px;
        &:last-child { border-bottom: none; }
        &:hover { background: #f5f5f5; }
      }
    }
  }
}

.page-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;

  @media (min-width: 768px) {
    padding: 20px 24px;
  }
}
</style>
